﻿export interface GoToTopOptions {
  buttonSelector?: string;
  scrollThreshold?: number;
  animationDuration?: number;
  visibleClass?: string;
  hiddenClass?: string;
  buttonHtml?: string;
  buttonTitle?: string;
}

export class GoToTop {
  private button!: HTMLButtonElement;
  private options: Required<GoToTopOptions>;
  private isVisible: boolean = false;

  constructor(options: GoToTopOptions = {}) {
    this.options = {
      buttonSelector: '.go-to-top',
      scrollThreshold: 300,
      animationDuration: 800,
      visibleClass: 'go-to-top--visible',
      hiddenClass: 'go-to-top--hidden',
      buttonHtml: '↑',
      buttonTitle: 'Go to top',
      ...options
    };

    this.initButton();
    this.addEventListeners();

    // Initialize as hidden on load
    this.hideButton();
  }

  private initButton(): void {
    const existingButton = document.querySelector(this.options.buttonSelector);

    this.button = existingButton instanceof HTMLButtonElement
      ? existingButton
      : this.createButton();
  }

  private createButton(): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = this.options.buttonSelector.slice(1);
    button.innerHTML = this.options.buttonHtml;
    button.title = this.options.buttonTitle;
    document.body.appendChild(button);
    return button;
  }

  private addEventListeners(): void {
    window.addEventListener('scroll', () => this.handleScroll());
    this.button.addEventListener('click', (e) => this.scrollToTop(e));
  }

  private handleScroll(): void {
    const shouldShow = window.scrollY > this.options.scrollThreshold;
    if (shouldShow !== this.isVisible) {
      this.isVisible = shouldShow;
      shouldShow ? this.showButton() : this.hideButton();
    }
  }
  private showButton(): void {
    this.button.classList.remove(this.options.hiddenClass);
    this.button.classList.add(this.options.visibleClass);
  }

  private hideButton(): void {
    this.button.classList.remove(this.options.visibleClass);
    this.button.classList.add(this.options.hiddenClass);
  }

  private scrollToTop(e: Event): void {
    e.preventDefault();
    const start = window.scrollY;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / this.options.animationDuration, 1);
      const ease = this.easeInOutQuad(progress);

      window.scrollTo(0, start - (start * ease));

      if (progress < 1) {
        window.requestAnimationFrame(animateScroll);
      }
    };

    window.requestAnimationFrame(animateScroll);
  }

  private easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }
}

declare global {
  interface Window {
    GoToTop: typeof GoToTop;
  }
}

if (typeof window !== 'undefined') {
  window.GoToTop = GoToTop;
}
